const STORAGE_PREFIX = 'pointra.chart.drawings.v1'
const MEASURE_PREFIX = 'pointra.chart.measure.v1'

export const INTERVAL_SECONDS = {
  '1m': 60,
  '3m': 180,
  '5m': 300,
  '15m': 900,
  '30m': 1800,
  '1h': 3600,
  '2h': 7200,
  '4h': 14400,
  '1d': 86400,
}

/** TradingView measure — up (long). */
export const TV_MEASURE_UP = '#2962FF'
/** TradingView measure — down (short). */
export const TV_MEASURE_DOWN = '#F23645'

/** @deprecated use measureColors() */
export const TV_MEASURE_COLOR = TV_MEASURE_UP

export function drawingsStorageKey(exchange, symbol, timeframe, measureOnly = false) {
  const prefix = measureOnly ? MEASURE_PREFIX : STORAGE_PREFIX
  return `${prefix}:${exchange}:${symbol}:${timeframe}`
}

export function loadDrawings(key, measureOnly = false) {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return measureOnly ? parsed.filter((d) => d?.type === 'measure') : parsed
  } catch {
    return []
  }
}

export function saveDrawings(key, drawings, measureOnly = false) {
  try {
    const payload = measureOnly ? drawings.filter((d) => d?.type === 'measure') : drawings
    localStorage.setItem(key, JSON.stringify(payload))
  } catch {
    /* quota */
  }
}

export function newDrawingId() {
  return `d_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
}

/**
 * @param {{ p: number }} p1
 * @param {{ p: number }} p2
 */
export function measureColors(p1, p2) {
  const up = p2.p >= p1.p
  return {
    stroke: up ? TV_MEASURE_UP : TV_MEASURE_DOWN,
    fill: up ? 'rgba(41, 98, 255, 0.14)' : 'rgba(242, 54, 69, 0.14)',
    up,
  }
}

/**
 * Count candles whose open time falls between two unix timestamps (inclusive).
 *
 * @param {number[]} candleTimes
 * @param {number} t1
 * @param {number} t2
 */
export function countBarsBetween(candleTimes, t1, t2, intervalSec = 3600) {
  const min = Math.min(t1, t2)
  const max = Math.max(t1, t2)
  const spanBars = Math.max(1, Math.round((max - min) / intervalSec))

  if (!candleTimes?.length) return spanBars

  const lastTime = candleTimes[candleTimes.length - 1]
  if (max > lastTime) {
    const inData = min <= lastTime
      ? candleTimes.filter((t) => t >= min && t <= Math.min(max, lastTime)).length
      : 0
    const futureStart = Math.max(min, lastTime)
    const futureBars = Math.max(0, Math.round((max - futureStart) / intervalSec))
    return Math.max(1, inData + futureBars)
  }

  const count = candleTimes.filter((t) => t >= min && t <= max).length
  return Math.max(1, count || spanBars)
}

/**
 * @param {number} seconds
 */
export function formatMeasureDuration(seconds) {
  const sec = Math.abs(seconds)
  if (sec >= 86400) {
    const d = sec / 86400
    return Number.isInteger(d) ? `${d}d` : `${d.toFixed(1)}d`
  }
  if (sec >= 3600) {
    const h = sec / 3600
    return Number.isInteger(h) ? `${h}h` : `${h.toFixed(1)}h`
  }
  if (sec >= 60) return `${Math.round(sec / 60)}m`
  return `${sec}s`
}

/**
 * TradingView-style measure stats.
 *
 * @param {{ t: number, p: number }} p1
 * @param {{ t: number, p: number }} p2
 * @param {string} timeframe
 * @param {number[]} [candleTimes]
 */
export function formatMeasureDetails(p1, p2, timeframe = '1h', candleTimes = []) {
  const priceDiff = p2.p - p1.p
  const pct = p1.p !== 0 ? (priceDiff / p1.p) * 100 : 0
  const sign = priceDiff >= 0 ? '+' : ''
  const intervalSec = INTERVAL_SECONDS[timeframe] || 3600
  const bars = countBarsBetween(candleTimes, p1.t, p2.t, intervalSec)
  const duration = formatMeasureDuration(p2.t - p1.t)

  return {
    priceLine: `${sign}${Math.abs(priceDiff).toFixed(2)} (${sign}${pct.toFixed(2)}%)`,
    barsLine: `${bars} bars, ${duration}`,
    priceDiff,
    bars,
    duration,
  }
}

/** @deprecated use formatMeasureDetails */
export function formatMeasureLabel(p1, p2, timeframe = '1h') {
  const d = formatMeasureDetails(p1, p2, timeframe)
  return `${d.priceLine} · ${d.barsLine}`
}

export function normalizeChartTime(raw) {
  if (raw == null) return null
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw
  if (typeof raw === 'string') {
    const parsed = Number.parseInt(raw, 10)
    return Number.isFinite(parsed) ? parsed : null
  }
  if (typeof raw === 'object' && raw.year && raw.month && raw.day) {
    return Math.floor(Date.UTC(raw.year, raw.month - 1, raw.day) / 1000)
  }
  return null
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} x
 * @param {number} y
 * @param {number} dirX
 * @param {number} dirY
 * @param {string} color
 */
function drawArrowHead(ctx, x, y, dirX, dirY, color) {
  const len = 5
  const angle = Math.atan2(dirY, dirX)
  ctx.save()
  ctx.strokeStyle = color
  ctx.lineWidth = 1.5
  ctx.setLineDash([])
  ctx.beginPath()
  ctx.moveTo(x, y)
  ctx.lineTo(x - len * Math.cos(angle - 0.45), y - len * Math.sin(angle - 0.45))
  ctx.moveTo(x, y)
  ctx.lineTo(x - len * Math.cos(angle + 0.45), y - len * Math.sin(angle + 0.45))
  ctx.stroke()
  ctx.restore()
}

/**
 * Draw TradingView-style measure box on canvas.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {{ x: number, y: number }} a origin (click 1)
 * @param {{ x: number, y: number }} b cursor / click 2
 * @param {{ t: number, p: number }} p1
 * @param {{ t: number, p: number }} p2
 * @param {string} timeframe
 * @param {number[]} candleTimes
 * @param {string} [color]
 * @param {number} cssW
 * @param {number} cssH
 */
export function drawTradingViewMeasure(
  ctx,
  a,
  b,
  p1,
  p2,
  timeframe,
  candleTimes,
  color,
  cssW,
  cssH,
) {
  const { stroke, fill, up } = measureColors(p1, p2)
  const accent = color || stroke
  const details = formatMeasureDetails(p1, p2, timeframe, candleTimes)
  const tagLines = [details.priceLine, details.barsLine]

  const tiny = Math.hypot(b.x - a.x, b.y - a.y) < 4

  ctx.save()

  if (tiny) {
    ctx.strokeStyle = 'rgba(120, 123, 134, 0.8)'
    ctx.lineWidth = 1
    ctx.setLineDash([4, 3])
    ctx.beginPath()
    ctx.moveTo(Math.max(0, a.x - 40), a.y)
    ctx.lineTo(Math.min(cssW, a.x + 40), a.y)
    ctx.moveTo(a.x, Math.max(0, a.y - 40))
    ctx.lineTo(a.x, Math.min(cssH, a.y + 40))
    ctx.stroke()
    drawTvMeasureTag(ctx, a.x, a.y + 26, ['0.00 (0.00%)', '0 bars, 0s'], accent, 'center')
    ctx.restore()
    return
  }

  const left = Math.min(a.x, b.x)
  const right = Math.max(a.x, b.x)
  const top = Math.min(a.y, b.y)
  const bottom = Math.max(a.y, b.y)
  const width = Math.max(1, right - left)
  const height = Math.max(1, bottom - top)

  ctx.fillStyle = fill
  ctx.fillRect(left, top, width, height)

  ctx.strokeStyle = accent
  ctx.lineWidth = 1
  ctx.setLineDash([])
  ctx.strokeRect(left, top, width, height)

  const innerX = up ? right : left
  const innerY = up ? top : bottom
  ctx.beginPath()
  ctx.moveTo(a.x, a.y)
  ctx.lineTo(innerX, innerY)
  ctx.stroke()

  ctx.setLineDash([])
  ctx.beginPath()
  ctx.moveTo(a.x, a.y)
  ctx.lineTo(innerX, a.y)
  ctx.moveTo(innerX, a.y)
  ctx.lineTo(innerX, innerY)
  ctx.stroke()

  if (up) {
    drawArrowHead(ctx, innerX, a.y, 1, 0, accent)
    drawArrowHead(ctx, innerX, innerY, 0, 1, accent)
  } else {
    drawArrowHead(ctx, innerX, a.y, -1, 0, accent)
    drawArrowHead(ctx, innerX, innerY, 0, -1, accent)
  }

  for (const pt of [a, b]) {
    ctx.beginPath()
    ctx.arc(pt.x, pt.y, 4, 0, Math.PI * 2)
    ctx.fillStyle = accent
    ctx.fill()
    ctx.strokeStyle = '#131722'
    ctx.lineWidth = 1.5
    ctx.stroke()
  }

  const tagY = up
    ? Math.max(4, top - 14)
    : Math.min(cssH - 4, bottom + 14)
  const tagX = (left + right) / 2
  drawTvMeasureTag(ctx, tagX, tagY, tagLines, accent, 'center')

  ctx.restore()
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} x
 * @param {number} y
 * @param {string[]} lines
 * @param {string} color
 * @param {'start' | 'center'} align
 */
function drawTvMeasureTag(ctx, x, y, lines, color, align) {
  const font = '11px -apple-system, BlinkMacSystemFont, "Trebuchet MS", sans-serif'
  ctx.font = font
  const lineHeight = 14
  const padX = 8
  const padY = 5
  const maxW = Math.max(...lines.map((l) => ctx.measureText(l).width))
  const boxW = maxW + padX * 2
  const boxH = lines.length * lineHeight + padY * 2 - 2
  const left = align === 'center' ? x - boxW / 2 : x
  const top = y - boxH / 2

  ctx.fillStyle = color
  ctx.beginPath()
  ctx.roundRect(left, top, boxW, boxH, 3)
  ctx.fill()

  ctx.fillStyle = '#ffffff'
  ctx.textBaseline = 'middle'
  lines.forEach((line, i) => {
    ctx.textAlign = 'center'
    ctx.fillText(line, left + boxW / 2, top + padY + lineHeight * i + lineHeight / 2 - 1)
  })
}
